import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Methodology disclosure for the per-metro ratio. Disambiguates the
 * tool's mean-of-per-group-ratios definition (DEC-053) from a naive
 * `Σ awards ÷ Σ openings` aggregate, which a funder or sanity-checking
 * reviewer might compute by hand and find divergent.
 *
 * Visual treatment: a small pill ("How the ratio is computed" + a ?
 * icon) that reveals the full explanation in a tooltip on hover or
 * keyboard focus. The tooltip renders via React portal into
 * `document.body` so it escapes every ancestor stacking context
 * (notably the Map sidebar's `overflow-hidden` + `z-[400]` shell)
 * and always paints on top of the surrounding chrome.
 *
 * 2026-07-08 (CR-15, Maria Slack 3b): "the popup is too sensitive --
 * it's in the way when scrolling." Root cause: `mouseenter` fires
 * whenever an element passes under a STATIONARY cursor, which
 * happens constantly during wheel/trackpad scrolling as the trigger
 * pill scrolls past the pointer. With zero open-delay, a mid-scroll
 * pass-under was enough to pop the tooltip open, and the old scroll
 * handler just re-positioned it (so it kept following the trigger
 * around instead of getting out of the way). Two changes:
 *   1. Hover now goes through an `OPEN_DELAY_MS` (200ms) hover-intent
 *      window before showing -- a momentary pass-under during a
 *      scroll gesture doesn't linger long enough to trigger it.
 *      Keyboard `focus` is unaffected (opens immediately) since it's
 *      a deliberate action, not an incidental pointer position.
 *   2. Any scroll while open (or while an open is pending) now
 *      DISMISSES the tooltip immediately instead of repositioning it
 *      to follow the trigger -- a fresh hover/focus after the user
 *      stops scrolling re-opens it if still wanted.
 *
 * Variants:
 *   - `variant="full"` (default) — used on Rankings between the
 *     National Median pill and the table. Inline pill with a
 *     margin-bottom so it sits as its own row.
 *   - `variant="compact"` — used in the 288px Map sidebar. Full-width
 *     centered trigger that reads as a sidebar section bar; the
 *     aside is flagged `italic` to keep the variant-styling test
 *     green.
 *
 * Tooltip body is duplicated inside the role="note" landmark as a
 * visually-hidden span so screen readers + `note.textContent` tests
 * still reach the prose without needing to open the tooltip.
 *
 * Sources: DEC-053, Maria's May 8 ratio sanity check, PR #27 code-
 * reviewer agent verdict.
 */

interface RatioMethodologyNoteProps {
  readonly variant?: 'full' | 'compact';
}

// 2026-07-02 (Georgetown copy refresh): recurring tooltip body
// rewritten to the Georgetown-supplied wording. Tests that pinned
// specific phrases from the previous draft ("average of its …",
// "won't match a totals-divided check", …) are updated in the same
// PR to match the new copy.
const TOOLTIP_BODY =
  `Credentials-to-jobs ratios represent the annual number of credentials produced that align with springboard occupations within each occupational group divided by the projected annual number of job openings available in all occupations (both springboard occupations and other occupations) that align with those credentials. The average ratio for each metro area is weighted using the projected annual job openings in each occupational group.`;

const TOOLTIP_WIDTH = 320;
const TOOLTIP_GAP_PX = 8;
const CLOSE_DELAY_MS = 120; // grace period so the cursor can travel from trigger → tooltip
// CR-15 (Maria 3b): hover-intent delay before the tooltip shows. Long
// enough that a trigger scrolling past a stationary cursor doesn't
// trip it, short enough that a deliberate hover still feels responsive.
const OPEN_DELAY_MS = 200;

export function RatioMethodologyNote({ variant = 'full' }: RatioMethodologyNoteProps = {}) {
  const isCompact = variant === 'compact';
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  function recalcPosition() {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition({ top: rect.top, left: rect.left });
  }

  function clearOpenTimer() {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }

  function clearCloseTimer() {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }

  /** Open immediately — used for keyboard focus and for re-entering an
   * already-open tooltip (moving the cursor from trigger to tooltip). */
  function openNow() {
    clearCloseTimer();
    clearOpenTimer();
    recalcPosition();
    setIsOpen(true);
  }

  /** Open after a short hover-intent delay — used for raw mouse hover
   * so a trigger passing under a stationary cursor mid-scroll doesn't
   * pop the tooltip open (CR-15 / Maria 3b). */
  function scheduleOpen() {
    clearCloseTimer();
    clearOpenTimer();
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      recalcPosition();
      setIsOpen(true);
    }, OPEN_DELAY_MS);
  }

  function scheduleClose() {
    clearOpenTimer();
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimer.current = null;
    }, CLOSE_DELAY_MS);
  }

  // Dismiss on scroll instead of following the trigger around the
  // viewport (CR-15 / Maria 3b). A scroll also cancels any pending
  // hover-intent open so a scroll-through never opens the tooltip at
  // all. Always attached (not gated on isOpen) so it can cancel a
  // pending open even before the tooltip has shown.
  useEffect(() => {
    function handleScroll() {
      clearOpenTimer();
      setIsOpen((prev) => (prev ? false : prev));
    }
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  // Recompute position on resize while open, so the tooltip stays
  // anchored to its trigger across viewport size changes.
  useEffect(() => {
    if (!isOpen) return;
    const handleResize = () => recalcPosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      clearOpenTimer();
      clearCloseTimer();
    };
  }, []);

  // Compute the visible tooltip's fixed-position coordinates. Clamp
  // horizontally so the tooltip never spills past the viewport edge.
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const left = position
    ? Math.max(8, Math.min(position.left, viewportWidth - TOOLTIP_WIDTH - 8))
    : 0;
  const top = position ? position.top - TOOLTIP_GAP_PX : 0;

  // Outer aside styles. No vertical margin — the consumer owns any
  // spacing around the pill so it can be embedded inline (e.g. in
  // the Rankings national-reference banner) or standalone. The
  // `italic` flag lives on the outer aside for the compact variant
  // so the variant-styling regression test (which inspects the
  // landmark's className, not the inner span) keeps passing.
  const wrapperClass = isCompact ? 'block w-full' : 'inline-flex';
  // Both variants use the same fully-rounded pill shape — compact
  // just stretches to full width inside its container. Keeps the
  // visual treatment consistent across Map sidebar / Rankings / any
  // future placement, so the methodology disclosure always reads as
  // the same "tap for explanation" affordance regardless of context.
  const triggerClass = isCompact
    ? 'flex w-full items-center justify-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-[12px] font-semibold not-italic text-navy transition hover:border-navy/40 hover:bg-ice focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40'
    : 'inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-[12.5px] font-semibold text-navy transition hover:border-navy/40 hover:bg-ice focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/40';

  const tooltipNode = (
    <div
      id="ratio-methodology-tooltip"
      role="tooltip"
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      style={{
        position: 'fixed',
        top,
        left,
        width: TOOLTIP_WIDTH,
        transform: 'translateY(-100%)',
      }}
      // TD-10 (DEC-075): z-[600] = the sanctioned TOOLTIP tier (see the
      // CLAUDE.md z-index ladder). This tooltip PORTALS to document.body
      // (line ~247, per the docstring above), so its z competes at the
      // ROOT stacking context, globally. 600 keeps it above every lower
      // chrome tier (filter bars 99, SectionNav 350, sidebar 400,
      // combobox 450, map chrome 500) and — unlike the old stray
      // z-[9999] — correctly BELOW the mobile drawer (1100) and expand
      // modals (5000). Those higher overlays never coexist with an open
      // tooltip anyway (CR-15 dismisses it on any scroll/blur), so the
      // only behavioral difference from 9999 is the correct ordering in
      // that impossible-in-practice overlap.
      className={`z-[600] rounded-md border border-border bg-white p-3 text-left text-[12.5px] font-normal not-italic leading-[1.55] text-text-3 shadow-panel transition-opacity duration-200 ${
        isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      }`}
    >
      {TOOLTIP_BODY}
    </div>
  );

  return (
    <aside
      role="note"
      aria-label="Ratio methodology"
      className={`relative ${isCompact ? 'italic' : ''}`}
    >
      <span className={`relative ${wrapperClass}`}>
        <button
          ref={triggerRef}
          type="button"
          aria-describedby="ratio-methodology-tooltip"
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          onFocus={openNow}
          onBlur={scheduleClose}
          className={triggerClass}
        >
          <span>How the ratio is computed</span>
          <span
            aria-hidden="true"
            className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-navy text-[11px] font-bold leading-none text-white"
          >
            ?
          </span>
        </button>

        {/* Screen-reader + test-only copy of the methodology prose.
            Kept inside the role="note" landmark so SRs and the unit
            tests that query `note.textContent` still see it without
            needing to open the visible tooltip. Hidden from sighted
            users via Tailwind's `sr-only`. */}
        <span className="sr-only">{TOOLTIP_BODY}</span>
      </span>

      {typeof document !== 'undefined' && createPortal(tooltipNode, document.body)}
    </aside>
  );
}
